import { Headline3, IconButton, Meta, Spinner, theme } from 'aimo-ui';
import { ButtonTypes } from 'aimo-ui/lib/typescript/src/components/Button/types';
import Icon from 'components/icons/Icon';
import {
  OngoingParkingHeaderStyles,
  StyledMeta,
} from 'components/ongoingParking/styles';
import { IconWrapper } from 'components/park/styles/styles';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useCustomBottomSheet from 'hooks/useCustomBottomSheet';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, Text } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { useParkingStore } from 'stores/parkingStore';
import styled from 'styled-components/native';
import { CenterRow } from 'styles/ContainerStyles';
import { OngoingSessionType } from 'types/common';
import { ParkingSession, ParkZone } from 'types/generatedSchemaTypes';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getLocationIconType, locationIcon } from 'utils/parkUtils';
import { resolveSegmentTitle } from 'utils/tariffUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { timeProgress } from 'utils/timeUtils';
import { useUiStore } from 'stores/uiStore';

type TimeSliderProps = {
  startTime: Date;
  endTime: Date | null;
  currentTime: Date;
  isExpanded: boolean;
};

const OngoingButton = styled.TouchableOpacity`
  background-color: ${({ theme: sTheme }) => sTheme.colors.gray300};
  width: 40px;
  height: 40px;
  border-radius: 40px;
  align-items: center;
  justify-content: center;
`;

const LoadingBackground = styled.View<{ isHeader?: boolean }>`
  width: ${({ isHeader }) => (isHeader ? 36 : 68)}px;
  height: ${({ isHeader }) => (isHeader ? 36 : 68)}px;
  background-color: ${theme.colors.white};
  align-items: center;
  border-radius: ${({ isHeader }) => (isHeader ? 36 : 68)}px;
  justify-content: center;
`;

const ExtendPressable = styled.TouchableOpacity`
  padding-top: 5px;
`;

const ProgressSlide = styled.View<{
  progress: number;
  isExpanded: boolean;
  overdue?: boolean;
}>`
  width: ${({ progress }) => progress}%;
  height: 6px;
  background-color: ${({ theme: bTheme, overdue }) =>
    overdue ? bTheme.colors.boost.red : bTheme.colors.boost.green};
  border-radius: ${({ isExpanded }) => (isExpanded ? 6 : 0)}px;
`;

const ButtonText = styled(Meta)`
  margin-top: ${theme.normalising.heightPixel(20)}px;
  color: ${({ theme: styledTheme }) => styledTheme.colors.gray100};
`;

const ButtonWrapper = styled.View`
  align-items: center;
`;

export const CollapseButton = () => {
  const handleClose = () => {
    useParkingStore.getState().setSelectedOngoingSession(undefined);
    Keyboard.dismiss();
  };

  return (
    <OngoingButton testID="collapse-ongoing-parking" onPress={handleClose}>
      <Icon name="Chevron_Down" color={theme.colors.white} />
    </OngoingButton>
  );
};

export const ListButton = () => {
  return (
    <OngoingButton
      testID="list-ongoing-sessions"
      onPress={() =>
        useUiStore.getState().setSheetModal('showOngoingMultipleSessions')
      }>
      <Icon name="List" color={theme.colors.white} />
    </OngoingButton>
  );
};

export const OpenSheetButton = ({ onPress }: { onPress: () => void }) => {
  return (
    <OngoingButton testID="collapse-ongoing-parking" onPress={onPress}>
      <Icon name="Chevron_Up" color={theme.colors.white} />
    </OngoingButton>
  );
};
export const TerminateButton = ({
  onPress,
  bigger = false,
  testID,
  disabled = false,
  action = 'STOP',
}: {
  onPress: () => void;
  bigger?: boolean;
  testID?: string;
  disabled?: boolean;
  action?: 'STOP' | 'CANCEL';
}) => {
  const { t } = useTranslation();

  return (
    <>
      <IconButton
        buttonType="alert"
        onPress={onPress}
        disabled={disabled}
        bigger={bigger}
        {...testIdentifiers(
          testID ?? `${action.toLocaleLowerCase()}-ongoing-parking`
        )}
        iconComponent={() => (
          <Icon
            name={action === 'STOP' ? 'Stop-Filled' : 'close'}
            size={bigger ? 's' : 'xs'}
            color={theme.colors.white}
          />
        )}
        rounded
        fullWidth={false}
        accessibilityLabel={t('button.close')}
      />
    </>
  );
};

export const ExtendButton = ({
  onPress,
  set = false,
  disabled = false,
}: {
  onPress: () => void;
  disabled?: boolean;
  set?: boolean;
}) => {
  return (
    <ExtendPressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      {...testIdentifiers(`${set ? 'set' : 'extend'}-ongoing-parking`)}>
      <Icon
        name={set ? 'Time-Outlined' : 'plus30'}
        size="xl"
        color={disabled ? theme.colors.gray50 : theme.colors.white}
      />
    </ExtendPressable>
  );
};

export const LoadingSpinner = ({
  isHeader = false,
}: {
  isHeader?: boolean;
}) => {
  return (
    <>
      <LoadingBackground isHeader={isHeader}>
        <Spinner size={isHeader ? 25 : 36} />
      </LoadingBackground>
    </>
  );
};

export const OngoingParkingTimeline: React.FC<TimeSliderProps> = ({
  startTime,
  endTime,
  isExpanded,
  currentTime,
}) => {
  const { animatedIndex } = useCustomBottomSheet();
  const progress = timeProgress(startTime, endTime, currentTime);
  const overdue = progress > 100;

  const containerAnimatedStyle = useAnimatedStyle(() => ({
    marginHorizontal: interpolate(
      animatedIndex.value,
      [0, 1],
      [0, 20],
      Extrapolation.CLAMP
    ),
    borderRadius: interpolate(
      animatedIndex.value,
      [0, 1],
      [0, 6],
      Extrapolation.CLAMP
    ),
  }));

  const containerStyle = useMemo(
    () => [
      OngoingParkingHeaderStyles.ongoingParkingSlide,
      containerAnimatedStyle,
    ],
    [containerAnimatedStyle]
  );

  return (
    <>
      <Animated.View style={containerStyle}>
        <ProgressSlide
          progress={progress}
          isExpanded={isExpanded}
          overdue={overdue}
          testID="parking-progress-slide"
        />
      </Animated.View>
    </>
  );
};

export const HeaderTexts = ({
  vehicleLabel,
  timeLeft,
  isManual = true,
  parkZoneName = '',
  isOverdue = false,
  isANPR = false,
  elapsedTime,
  extended = false,
}: {
  vehicleLabel?: string;
  timeLeft: string;
  isManual?: boolean;
  parkZoneName?: string | null;
  isOverdue?: boolean;
  isANPR?: boolean;
  elapsedTime?: string;
  extended?: boolean;
}) => {
  const { t } = useTranslation();

  const renderTexts = () => {
    if ((isANPR || isManual) && extended) {
      return parkZoneName;
    }
    if (isManual && timeLeft && !isOverdue) {
      return t('parking.ongoingTitle') + ' ' + timeLeft;
    }
    if (isManual && isOverdue) {
      return t('parking.parkingEnding');
    }
    if (isANPR) {
      return t('parking.ongoingTitle') + ' • ' + elapsedTime;
    }
    return parkZoneName;
  };
  return (
    <>
      <Headline3
        numberOfLines={1}
        color={theme.colors.white}
        {...testIdentifiers('parking-time-left')}>
        {renderTexts()}
      </Headline3>
      <Meta
        color={theme.colors.white}
        numberOfLines={1}
        {...testIdentifiers('parking-time-vehicle')}>
        {vehicleLabel}
      </Meta>
    </>
  );
};

export const ChargingHeaderTexts = ({
  title,
  vehicleLabel,
}: {
  title: string;
  vehicleLabel: string;
}) => {
  return (
    <>
      <Headline3
        numberOfLines={1}
        color={theme.colors.white}
        testID="charging-modal-header-text">
        {title}
      </Headline3>
      <Meta
        color={theme.colors.white}
        numberOfLines={1}
        testID="parking-time-vehicle">
        {vehicleLabel}
      </Meta>
    </>
  );
};

type RoundButtonProps = {
  onPress: () => void;
  disabled?: boolean;
  testID?: string;
  name: string;
  type: ButtonTypes;
  buttonText?: string;
};

export const RoundButton = ({
  onPress,
  disabled = false,
  testID,
  name,
  type,
  buttonText,
}: RoundButtonProps) => {
  const { t } = useTranslation();

  return (
    <ButtonWrapper>
      <IconButton
        buttonType={type}
        onPress={onPress}
        disabled={disabled}
        bigger
        {...testIdentifiers(`${testID}-button`)}
        iconComponent={() => (
          <Icon name={name} size="m" color={theme.colors.white} />
        )}
        rounded
        fullWidth={false}
        accessibilityLabel={t('button.close')}
      />
      {buttonText && (
        <ButtonText testID={`${testID}-text`}>{buttonText}</ButtonText>
      )}
    </ButtonWrapper>
  );
};

export const permitCoversParking = (session: ParkingSession): boolean => {
  const sessionStartDate = session.startDate
    ? new Date(session.startDate)
    : null;

  const sessionEndDate = (() => {
    if (session.predefinedEndDate) {
      return new Date(session.predefinedEndDate);
    }
    if (session.parkingAllowedUntil) {
      return new Date(session.parkingAllowedUntil);
    }
    return null;
  })();

  const isFreeOrCompanyPaidPoolSession: boolean =
    session.poolingInfo?.poolParkingType === 'COMPANY_PAYS' ||
    session.poolingInfo?.poolParkingType === 'FREE';

  const userHasLTPPermit: boolean =
    session.permitAllocations?.some((allocation) => {
      if (!allocation) return false;

      const allocationFrom = allocation.from ? new Date(allocation.from) : null;
      const allocationTo = allocation.to ? new Date(allocation.to) : null;

      const isStartDateCovered =
        allocationFrom &&
        sessionStartDate &&
        allocationFrom <= sessionStartDate; //making sure that the allocation start time is before or same time as the session start time
      const isEndDateCovered =
        !allocationTo || (sessionEndDate && allocationTo >= sessionEndDate); //making sure that allocation end time is after the session or same time as session end time
      const isTypeMatching = allocation.type === 'CALCULATION_OVERRIDE';

      return isStartDateCovered && isEndDateCovered && isTypeMatching;
    }) ?? false;
  return userHasLTPPermit || Boolean(isFreeOrCompanyPaidPoolSession);
};

export const SessionPriceInfo: React.FC<{ session: ParkingSession }> = ({
  session,
}) => {
  const { t } = useTranslation();
  const { pricingSegments } = useParkingPricingInfo(
    session.parkingZoneUid || ''
  );
  const segmentTitle = resolveSegmentTitle(
    pricingSegments,
    session.customerSegment?.uid
  );

  if (!session.poolingInfo) {
    return (
      <CenterRow isVerticalCenter>
        <StyledMeta>
          <Text>{segmentTitle ?? ''}</Text>
        </StyledMeta>
      </CenterRow>
    );
  }

  const insidePool = !!session.poolingInfo?.pooled;
  const isUserPays = session.poolingInfo?.poolParkingType === 'USER_PAYS';
  const insidePoolText = t('parking.receipt.insidePool');
  const outsidePoolText = t('parking.receipt.outsidePool');

  let infoText: string;
  if (insidePool) {
    infoText = isUserPays
      ? `${insidePoolText} • ${segmentTitle}`
      : insidePoolText;
  } else {
    infoText = isUserPays
      ? `${outsidePoolText} • ${segmentTitle}`
      : outsidePoolText;
  }

  return (
    <CenterRow isVerticalCenter>
      <StyledMeta>
        <Text>{infoText}</Text>
      </StyledMeta>
    </CenterRow>
  );
};

export const OngoingSessionsIcons = ({
  sessionsByParkZone,
}: {
  sessionsByParkZone: OngoingSessionType[];
}) => {
  const {
    flags: { anpr },
  } = useContext(FeatureFlagsContext);
  const showCameraRecognition = showFeatureFlag(anpr);

  const hasChargingSession =
    sessionsByParkZone?.filter(
      (session: OngoingSessionType) => session?.chargingSessionId
    )?.length > 0;

  const getLocationIconName = (parkZone: ParkZone) => {
    return getLocationIconType(
      parkZone.locationType || 'OTHER',
      showCameraRecognition && parkZone.parkingMethods?.anpr
    );
  };

  return (
    <>
      {sessionsByParkZone[0].parkZone && (
        <IconWrapper mr={hasChargingSession ? 0 : 3}>
          <Icon
            name={
              locationIcon[getLocationIconName(sessionsByParkZone[0].parkZone)]
            }
            size="s"
            testID={`location-icon-${getLocationIconName(
              sessionsByParkZone[0].parkZone
            )}`}
            color={theme.colors.boost.green}
          />
        </IconWrapper>
      )}
      {hasChargingSession && (
        <IconWrapper mr={3}>
          <Icon
            name={locationIcon.evCharging}
            size="s"
            testID="location-icon-Charging"
            color={theme.colors.boost.green}
          />
        </IconWrapper>
      )}
    </>
  );
};
